import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";
import { articles, categories } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      "@type": "Organization",
      name: "B站暴躁少女CSGO视频专题站",
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={`B站暴躁少女CSGO视频,${article.tags.join(",")},CSGO,暴躁少女`}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        articleData={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author,
          tags: article.tags,
        }}
      />

      <Navbar />

      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Article Header */}
          <header className="py-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-5 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次观看
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-thumb-up-line"></i>
                {article.likes >= 10000 ? `${(article.likes / 10000).toFixed(1)}万` : article.likes} 赞
              </span>
            </div>
          </header>

          {/* Feature Image */}
          <div className="mb-10">
            <div className="w-full aspect-video rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`B站暴躁少女CSGO视频 ${article.title}`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="prose-custom mb-14">
            {article.content.split("\n\n").map((paragraph, index) => (
              <p key={index} className="text-foreground-700 leading-relaxed mb-5 text-base">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {/* Share Section */}
          <div className="border-t border-background-200 pt-6 pb-4 flex items-center gap-4 flex-wrap">
            <span className="text-sm text-foreground-600 font-medium">分享文章：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap" aria-label="分享到微博">
              <i className="ri-weibo-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap" aria-label="分享到微信">
              <i className="ri-wechat-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap" aria-label="复制链接">
              <i className="ri-link"></i>
            </button>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-14 border-t border-background-200">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard
                    key={a.id}
                    slug={a.slug}
                    title={a.title}
                    excerpt={a.excerpt}
                    image={a.image}
                    date={a.date}
                    categoryName={a.categoryName}
                    categorySlug={a.category}
                    tags={a.tags}
                    views={a.views}
                    likes={a.likes}
                  />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}